import { Link } from "react-router-dom";
import { Mail } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";

// Custom Instagram icon (more elaborate)
const InstagramIcon = ({ className }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
    <circle cx="12" cy="12" r="3" strokeWidth="1" />
  </svg>
);

export const Footer = () => {
  const { t } = useLanguage();

  return (
    <footer className="bg-[#2c4c3b] text-stone-100" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="md:col-span-2">
            <div className="mb-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_physio-letters/artifacts/3vkvm70p__Blue%20and%20Red%20Flat%20Minimal%20Write%20Club%20Logo.png"
                alt="Phisioletters Mail Club"
                className="h-16 w-auto object-contain brightness-0 invert"
              />
            </div>
            <p className="text-stone-300 leading-relaxed max-w-md text-sm">
              {t('footer.description')}
            </p>
          </div>

          {/* Links */}
          <div>
            <h4 className="font-serif text-lg mb-6 text-stone-100">{t('footer.navigate')}</h4>
            <ul className="space-y-3">
              <li>
                <Link
                  to="/#storia"
                  className="text-stone-300 hover:text-stone-100 transition-colors text-sm"
                  data-testid="footer-link-storia"
                >
                  {t('nav.myStory')}
                </Link>
              </li>
              <li>
                <Link
                  to="/#ecosistema"
                  className="text-stone-300 hover:text-stone-100 transition-colors text-sm"
                  data-testid="footer-link-ecosistema"
                >
                  {t('nav.ecosystem')}
                </Link>
              </li>
              <li>
                <a
                  href="https://skool.com/phisioletters-9232"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-stone-300 hover:text-stone-100 transition-colors text-sm"
                  data-testid="footer-link-skool"
                >
                  {t('footer.communitySkool')}
                </a>
              </li>
              <li>
                <Link
                  to="/pricing"
                  className="text-stone-300 hover:text-stone-100 transition-colors text-sm"
                  data-testid="footer-link-piani"
                >
                  {t('nav.plans')}
                </Link>
              </li>
              <li>
                <Link
                  to="/booking"
                  className="text-stone-300 hover:text-stone-100 transition-colors text-sm"
                  data-testid="footer-link-prenota"
                >
                  {t('nav.book')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact & Social */}
          <div>
            <h4 className="font-serif text-lg mb-6 text-stone-100">{t('footer.connect')}</h4>
            <div className="space-y-4">
              <a
                href="https://www.instagram.com/phisioletters/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 text-stone-300 hover:text-stone-100 transition-colors group"
                data-testid="instagram-main-link"
              >
                <div className="w-10 h-10 rounded-full bg-stone-100/10 flex items-center justify-center group-hover:bg-stone-100/20 transition-colors">
                  <InstagramIcon className="w-5 h-5" />
                </div>
                <span className="text-sm">@phisioletters</span>
              </a>
              <a
                href="https://www.instagram.com/cuorefisio/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 text-stone-300 hover:text-stone-100 transition-colors group"
                data-testid="instagram-secondary-link"
              >
                <div className="w-10 h-10 rounded-full bg-stone-100/10 flex items-center justify-center group-hover:bg-stone-100/20 transition-colors">
                  <InstagramIcon className="w-5 h-5" />
                </div>
                <span className="text-sm">@cuorefisio</span>
              </a>
              <a
                href="mailto:info@phisioletters.com"
                className="flex items-center gap-3 text-stone-300 hover:text-stone-100 transition-colors group"
                data-testid="email-link"
              >
                <div className="w-10 h-10 rounded-full bg-stone-100/10 flex items-center justify-center group-hover:bg-stone-100/20 transition-colors">
                  <Mail className="w-5 h-5" strokeWidth={1} />
                </div>
                <span className="text-sm">info@phisioletters.com</span>
              </a>
            </div>
          </div>
        </div>

        {/* Bottom */}
        <div className="mt-16 pt-8 border-t border-stone-100/10 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-stone-400 text-xs">
            © {new Date().getFullYear()} Phisioletters di Marlene Brochero. {t('footer.rights')}
          </p>
          <div className="flex items-center gap-6">
            <Link
              to="/privacy"
              className="text-stone-400 hover:text-stone-300 text-xs transition-colors"
            >
              {t('footer.privacy')}
            </Link>
            <Link
              to="/terms"
              className="text-stone-400 hover:text-stone-300 text-xs transition-colors"
            >
              {t('footer.terms')}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
